import React from 'react'
import { observer } from 'mobx-react'
import cx from 'classnames'
import Avatar from 'components/Avatar'
import Icon from 'components/Icon'
import styles from './TableCell.sass'

/**
 * Column (row?) density presets.
 */
export enum ColumnDensity {
  Default,
  Comfortable,
  Compact,
}

export interface CellProps {
  children: React.ReactNode
}

export interface DataTestProps {
  dataTest?: string
}

export const TextCellContent = observer(function TextCell({
  children,
  dataTest,
}: CellProps & DataTestProps) {
  return <span data-test={dataTest}>{children}</span>
})

const defaultDateFormatter = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
})

const dateFormatterCompact = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
})

const timeFormatter = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: 'numeric',
  hour12: true,
})

export type FormatDate = (date?: Date | number) => string

interface DateDisplayProps {
  date: Date
  density: ColumnDensity
  format?: FormatDate
}

/**
 * Correctly formats dates of the form [Month] [Day], [Year] for table cells.
 */
export function DateDisplay({ date, density, format }: DateDisplayProps) {
  const fullSize = density === ColumnDensity.Default
  const compact = density === ColumnDensity.Compact
  const formatDate: FormatDate =
    format ||
    (compact ? dateFormatterCompact.format : defaultDateFormatter.format)

  return (
    <>
      <span
        className={cx(
          styles.dateLine,
          fullSize ? styles.medium : styles.normal,
        )}
      >
        {formatDate(date)}
      </span>
      {fullSize && (
        <span className={cx(styles.timeLine)}>
          {timeFormatter.format(date)}
        </span>
      )}
    </>
  )
}

export interface ImageCellProps {
  title?: string | null
  image?: string | null
  imageFallback?: string | null
  size?: number
  minWidth?: number
  noPadding?: boolean
  dataTest?: string
  online?: boolean
}

export const ImageCellContent = observer(function ImageCellContent({
  title,
  image,
  imageFallback,
  size,
  minWidth,
  noPadding,
  dataTest,
  online,
}: ImageCellProps) {
  return (
    <>
      {(image || title) && (
        <div className={styles.primaryCellContainer} style={{ minWidth }}>
          <div
            className={cx(styles.imageContainer, noPadding && styles.noPadding)}
          >
            <Avatar
              size={size ?? 20}
              subject={{
                avatar: image,
                avatarFallback: imageFallback,
              }}
            />
          </div>
          <div className={styles.textCell} data-test={dataTest}>
            {title}
          </div>
          {online && (
            <div
              className={cx(styles.presenceIndicator, online && styles.online)}
            />
          )}
        </div>
      )}
    </>
  )
})

export interface CheckCellProps {
  checked?: boolean
  color?: string
}

export const CheckCellContent = observer(function CheckCellContent({
  checked,
  color,
}: CheckCellProps) {
  return (
    <div className={styles.textCenter}>
      {checked ? <Icon name="tick" size={24} color={color} /> : null}
    </div>
  )
})
